import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Calendar, CheckSquare, Clock, MapPin, Plus } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (sameDay(d, today)) return `Today · ${time}`;
  if (sameDay(d, tomorrow)) return `Tomorrow · ${time}`;
  return d.toLocaleDateString([], { month: "short", day: "numeric" }) + " · " + time;
}

function priorityChip(p) {
  const colors = {
    high: "bg-rose-100 text-rose-800",
    medium: "bg-amber-100 text-amber-800",
    low: "bg-slate-100 text-slate-700",
  };
  return colors[p] || colors.medium;
}

export default function UpcomingScheduleWidget() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/calendar/upcoming?limit=6");
      setItems(res.data?.items || []);
    } catch (e) {
      // silent — widget hides gracefully
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const intv = setInterval(load, 60000);
    return () => clearInterval(intv);
  }, [load]);

  const completeTodo = async (id) => {
    try {
      await api.post(`/todos/${id}/complete`);
      toast.success("Marked done");
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="upcoming-schedule-widget">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-500" />
          <span className="font-semibold text-slate-900 text-sm">Upcoming schedule</span>
        </div>
        <Link
          to="/dashboard/schedule"
          data-testid="upcoming-widget-open-schedule"
          className="text-xs text-slate-600 hover:text-slate-900"
        >
          Open →
        </Link>
      </div>
      <div className="divide-y divide-slate-100" data-testid="upcoming-widget-list">
        {loading && <div className="p-5 text-sm text-slate-500">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="p-6 text-center text-sm text-slate-500" data-testid="upcoming-widget-empty">
            <div className="mb-3">No upcoming items.</div>
            <Link
              to="/dashboard/schedule"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800"
            >
              <Plus className="w-3.5 h-3.5" /> Plan your week
            </Link>
          </div>
        )}
        {!loading &&
          items.map((it) => (
            <div
              key={`${it.kind}-${it.id}`}
              className="px-4 py-3 flex items-start gap-3 hover:bg-slate-50"
              data-testid={`upcoming-widget-item-${it.id}`}
            >
              <div className="mt-0.5">
                {it.kind === "appointment" ? (
                  <Calendar className="w-4 h-4 text-emerald-600" />
                ) : (
                  <CheckSquare className="w-4 h-4 text-amber-600" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900 text-sm truncate">{it.title}</div>
                <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {fmtWhen(it.at)}
                  </span>
                  {it.location && (
                    <span className="inline-flex items-center gap-1 truncate">
                      <MapPin className="w-3 h-3" /> {it.location}
                    </span>
                  )}
                  {it.kind === "todo" && (
                    <span className={`px-1.5 py-0.5 rounded ${priorityChip(it.priority)}`}>
                      {it.priority}
                    </span>
                  )}
                </div>
              </div>
              {it.kind === "todo" && (
                <button
                  onClick={() => completeTodo(it.id)}
                  data-testid={`upcoming-widget-complete-${it.id}`}
                  className="text-xs text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50 rounded px-2 py-1"
                  title="Mark done"
                >
                  Done
                </button>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}
